import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, X, Send, Sparkles, Loader2, LifeBuoy, ArrowLeft, Paperclip } from "lucide-react";

const sid = () => {
  let s = localStorage.getItem("cr_chat_sid");
  if (!s) { s = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("cr_chat_sid", s); }
  return s;
};

const SupportChat = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("chat"); // chat | ticket
  const [messages, setMessages] = useState([
    { role: "assistant", body: "Hey! I'm Clouty 👋 Ask me anything about CloutRise — plans, AI Studio, growth tips. Or leave a message for the team." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [chatAttach, setChatAttach] = useState(null);
  const [uploadingChat, setUploadingChat] = useState(false);
  const [ticket, setTicket] = useState({ subject: "", message: "", name: "", email: "" });
  const [ticketAttach, setTicketAttach] = useState(null);
  const [uploadingTicket, setUploadingTicket] = useState(false);
  const [sendingTicket, setSendingTicket] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { setTicket((t) => ({ ...t, name: user?.name || t.name, email: user?.email || t.email })); }, [user]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, open, busy]);

  const send = async () => {
    const text = input.trim();
    if ((!text && !chatAttach) || busy) return;
    const msg = text || "📎 (image)";
    setMessages((m) => [...m, { role: "user", body: msg, attachment_path: chatAttach?.path }]);
    setInput("");
    const sentAttach = chatAttach;
    setChatAttach(null);
    setBusy(true);
    try {
      const { data } = await api.post("/chat", {
        session_id: sid(), message: msg,
        attachment_path: sentAttach?.path, attachment_name: sentAttach?.name, attachment_type: sentAttach?.type,
      });
      setMessages((m) => [...m, { role: "assistant", body: data.reply }]);
      if (data.escalate && !data.ticket_created) {
        setTicket((t) => ({ ...t, subject: t.subject || msg.slice(0, 60), message: t.message || msg }));
        setTimeout(() => setMode("ticket"), 700);
      }
    } catch {
      setMessages((m) => [...m, { role: "assistant", body: "Sorry, I couldn't answer just now. Try again or leave a message for the team." }]);
    } finally { setBusy(false); }
  };

  const uploadChatFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingChat(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/support/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setChatAttach(data);
    } catch (e2) { toast.error(e2.response?.data?.detail || "Upload failed"); }
    finally { setUploadingChat(false); e.target.value = ""; }
  };

  const submitTicket = async () => {
    if (!ticket.subject.trim() || !ticket.message.trim()) { toast.error("Add a subject and message"); return; }
    if (!user && !ticket.email.trim()) { toast.error("Please add your email so we can reply"); return; }
    setSendingTicket(true);
    try {
      await api.post("/support/tickets", {
        subject: ticket.subject, message: ticket.message,
        name: ticket.name || undefined, email: user ? undefined : ticket.email,
        attachment_path: ticketAttach?.path, attachment_name: ticketAttach?.name, attachment_type: ticketAttach?.type,
      });
      toast.success("Message sent! We'll reply by email — our AI answers within 24h if the team is away.");
      setTicket({ subject: "", message: "", name: user?.name || "", email: user?.email || "" });
      setTicketAttach(null);
      setMode("chat");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send message"); }
    finally { setSendingTicket(false); }
  };

  const uploadTicketFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingTicket(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/support/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTicketAttach(data);
    } catch (e2) { toast.error(e2.response?.data?.detail || "Upload failed"); }
    finally { setUploadingTicket(false); e.target.value = ""; }
  };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="support-chat-launcher"
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-primary text-background shadow-[0_0_28px_rgba(0,242,254,0.45)] flex items-center justify-center hover:scale-105 transition-transform">
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            className="fixed bottom-24 right-5 z-[60] w-[92vw] max-w-[380px] h-[560px] max-h-[78vh] rounded-2xl border border-white/10 bg-[#0d0d10] shadow-2xl flex flex-col overflow-hidden"
            data-testid="support-chat-panel">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-gradient-to-r from-primary/15 to-transparent">
              <div className="w-9 h-9 rounded-full bg-primary/20 text-primary flex items-center justify-center"><Sparkles className="w-4.5 h-4.5" /></div>
              <div className="flex-1">
                <div className="font-heading font-bold text-sm">Clouty · CloutRise Support</div>
                <div className="text-[11px] text-primary flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-primary inline-block" /> Online · AI-powered</div>
              </div>
            </div>

            {mode === "chat" ? (
              <>
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="support-chat-messages">
                  {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-line ${m.role === "user" ? "bg-primary text-background rounded-br-sm" : "bg-white/5 border border-white/10 rounded-bl-sm"}`}>
                        {m.body}
                        {m.attachment_path && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/support/files/${m.attachment_path}`} target="_blank" rel="noreferrer" className="block mt-2 rounded-lg overflow-hidden bg-black/30 p-1 w-fit">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/support/files/${m.attachment_path}`} alt="attachment" className="max-h-40 rounded-md" data-testid="chat-attachment" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                  {busy && <div className="flex justify-start"><div className="bg-white/5 border border-white/10 rounded-2xl rounded-bl-sm px-3.5 py-2.5"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div></div>}
                </div>
                <div className="px-3 pb-2">
                  <button onClick={() => setMode("ticket")} className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 mb-2" data-testid="support-message-team-btn">
                    <LifeBuoy className="w-3.5 h-3.5" /> Message the team instead
                  </button>
                  {chatAttach && (
                    <div className="mb-2 inline-flex items-center gap-2 text-xs bg-white/5 border border-white/10 rounded-lg px-2 py-1" data-testid="chat-attach-preview">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/support/files/${chatAttach.path}`} alt="preview" className="w-8 h-8 rounded object-cover" />
                      <span className="max-w-[150px] truncate">{chatAttach.name}</span>
                      <button onClick={() => setChatAttach(null)} data-testid="chat-remove-attach"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <label className="h-11 w-10 shrink-0 rounded-full border border-white/10 flex items-center justify-center cursor-pointer hover:border-primary/40" data-testid="chat-attach-btn">
                      {uploadingChat ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                      <input type="file" accept="image/*" className="hidden" onChange={uploadChatFile} data-testid="chat-file-input" />
                    </label>
                    <Textarea value={input} onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder="Ask a question…" rows={1} className="resize-none bg-white/5 border-white/10 min-h-[44px] max-h-28" data-testid="support-chat-input" />
                    <Button onClick={send} disabled={busy || (!input.trim() && !chatAttach)} className="h-11 w-11 shrink-0 rounded-full bg-primary text-background p-0" data-testid="support-chat-send">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="support-ticket-form">
                <button onClick={() => setMode("chat")} className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1" data-testid="support-back-to-chat">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to chat
                </button>
                <p className="text-sm text-muted-foreground">Leave a message and we'll reply by email. If the team is away, our AI responds within 24 hours.</p>
                {!user && (
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={ticket.name} onChange={(e) => setTicket({ ...ticket, name: e.target.value })} placeholder="Your name" className="bg-white/5 border-white/10 h-10" data-testid="ticket-name-input" />
                    <Input value={ticket.email} onChange={(e) => setTicket({ ...ticket, email: e.target.value })} placeholder="Email" type="email" className="bg-white/5 border-white/10 h-10" data-testid="ticket-email-input" />
                  </div>
                )}
                <Input value={ticket.subject} onChange={(e) => setTicket({ ...ticket, subject: e.target.value })} placeholder="Subject" className="bg-white/5 border-white/10 h-10" data-testid="ticket-subject-input" />
                <Textarea value={ticket.message} onChange={(e) => setTicket({ ...ticket, message: e.target.value })} placeholder="How can we help?" className="bg-white/5 border-white/10 min-h-[120px]" data-testid="ticket-message-input" />
                <label className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-primary cursor-pointer" data-testid="ticket-attach-btn">
                  <Paperclip className="w-3.5 h-3.5" /> {uploadingTicket ? "Uploading…" : ticketAttach ? "Change screenshot" : "Attach a screenshot"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadTicketFile} data-testid="ticket-file-input" />
                </label>
                {ticketAttach && (
                  <div className="flex items-center gap-2 text-xs" data-testid="ticket-attach-preview">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/support/files/${ticketAttach.path}`} alt="preview" className="w-10 h-10 rounded object-cover border border-white/10" />
                    <span className="truncate max-w-[160px]">{ticketAttach.name}</span>
                    <button onClick={() => setTicketAttach(null)} data-testid="ticket-remove-attach"><X className="w-3.5 h-3.5" /></button>
                  </div>
                )}
                <Button onClick={submitTicket} disabled={sendingTicket} className="w-full rounded-full bg-primary text-background font-bold" data-testid="ticket-submit-btn">
                  {sendingTicket ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send message"}
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SupportChat;
